<script setup>
import Buttons from '@/components/ui/Buttons.vue';
defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
</script>
<template>
  <section
    :id="data.id"
    class="section"
    :class="[
      data?.background_color ? `bg-${data.background_color}` : '',
      data?.spacing?.top
        ? `section--top-${data.spacing.top}`
        : 'section--top-xl',
      data?.spacing?.bottom
        ? `section--bottom-${data.spacing.bottom}`
        : 'section--bottom-xl',
      data?.background_color === 'black' ? 'white' : '',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="fuel-cards">
        <div class="fuel-cards__top">
          <h2
            v-if="data.title"
            class="mb-0 fuel-cards__title display text-center"
            :class="[data?.background_color === 'black' ? 'white' : 'black']"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="data.title"
          ></h2>
          <NuxtImg
            v-if="data?.image?.src"
            :src="data?.image?.src"
            :alt="data?.image?.title"
            :height="data?.image?.height"
            :width="data?.image?.width"
            class="fuel-cards__image"
            data-aos="zoom-in"
            data-aos-easing="ease-out-quad"
            loading="lazy"
          />
        </div>
        <div class="fuel-cards__bottom">
          <div
            v-if="data.description"
            class="fuel-cards__description body-1-regular text-center"
            :class="[data?.background_color === 'black' ? 'white' : 'black']"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="data.description"
          ></div>
          <ul
            v-if="data.items && data.items.length > 0"
            :class="[
              `m-0 p-0 fuel-cards__items fuel-cards__items--${data?.columns_count}`,
            ]"
          >
            <li
              v-for="(item, itemIndex) in data.items"
              :key="itemIndex"
              class="m-0 fuel-cards__item"
              data-aos="fade-up"
              :data-aos-delay="itemIndex >= 1 ? 100 + 100 * itemIndex : 100"
            >
              <NuxtImg
                v-if="item?.icon?.src"
                :src="item?.icon?.src"
                :alt="item?.icon?.alt"
                :width="item?.icon?.width"
                :height="item?.icon?.height"
                class="fuel-cards__item-image"
                loading="lazy"
              />
              <span
                class="fuel-cards__item-title desktop-body-1-bold"
                :class="[
                  data?.background_color === 'black' ? 'white' : 'black',
                ]"
                v-html="item.title"
              ></span>
            </li>
          </ul>
          <Buttons
            v-if="data?.buttons && data?.buttons.length > 0"
            class="fuel-cards__buttons"
            :buttons="data?.buttons"
            :background-color="data?.background_color"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            :buttons-prefix-class="'js-button-fuel-cards'"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.fuel-cards {
  display: flex;
  flex-direction: column;
  gap: 80px;
  margin: 0 auto;
  width: 100%;
  @include media-breakpoint-up(sm) {
    gap: 120px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 120);
  }
  &__top {
    display: flex;
    flex-direction: column;
    gap: 56px;
    @include media-breakpoint-up(sm) {
      gap: 80px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 80);
    }
  }
  &__title {
    margin: 0 auto;
    text-align: center;
    @include media-breakpoint-up(md) {
      width: 66%;
    }
  }
  &__image {
    border-radius: 18px;
    border-image-source: linear-gradient(
      327.03deg,
      rgba(255, 255, 255, 0.11) -4.18%,
      rgba(255, 255, 255, 0) 80.94%
    );
    box-shadow: 0px 52px 44px 0px #00000059;
    height: auto;
    max-width: 550px;
    margin: 0 auto;
    width: 100%;
    @include media-breakpoint-up(sm) {
      border-radius: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 24);
      @include responsive-spacing(max-width, 550);
    }
  }
  &__bottom {
    display: flex;
    flex-direction: column;
    gap: 56px;
    @include media-breakpoint-up(sm) {
      gap: 80px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 80);
    }
  }
  &__description {
    margin: 0 auto;
    &:deep(p:last-child) {
      margin: 0;
    }
    @include media-breakpoint-up(sm) {
      max-width: 80.3%;
    }
    @include media-breakpoint-up(md) {
      max-width: 49.4%;
    }
  }
  &__items {
    display: grid;
    gap: 32px;
    grid-template-columns: 1fr;
    padding: 0;
    list-style: none;
    @include media-breakpoint-up(sm) {
      column-gap: 40px;
      row-gap: 48px;
      &--4 {
        grid-template-columns: repeat(2, 1fr);
      }
      &--5 {
        grid-template-columns: repeat(3, 1fr);
      }
    }
    @include media-breakpoint-up(md) {
      &--4 {
        grid-template-columns: repeat(4, 1fr);
      }
      &--5 {
        grid-template-columns: repeat(5, 1fr);
      }
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(column-gap, 40);
      @include responsive-spacing(row-gap, 48);
    }
  }
  &__item {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 0;
    padding: 0 24px;
    text-align: center;
    @include media-breakpoint-up(sm) {
      gap: 24px;
      padding: 0;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
    }
  }
  &__buttons {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    @include media-breakpoint-up(sm) {
      flex-direction: row;
      gap: 16px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
  }
}
</style>
